/**
 * Canonical training-video storage keys and authenticated file URLs.
 * Keys live under trainings/{tenantId}/videos/{filename}.
 */

const FILES_PREFIX = '/api/files/';

function stripQuery(value) {
    const raw = String(value || '').trim();
    const q = raw.indexOf('?');
    return q === -1 ? raw : raw.slice(0, q);
}

/**
 * @param {unknown} urlOrKey
 * @returns {string}
 */
export function toTrainingVideoStorageKey(urlOrKey) {
    let key = stripQuery(urlOrKey).replace(/\\/g, '/');
    if (!key) return '';
    try {
        if (/^https?:\/\//i.test(key)) {
            key = new URL(key).pathname;
        }
    } catch {
        /* keep raw */
    }
    key = key.replace(/^\/+/, '');
    if (key.startsWith('api/files/')) key = key.slice('api/files/'.length);
    else if (key.startsWith('files/')) key = key.slice('files/'.length);
    else if (key.startsWith('uploads/')) key = key.slice('uploads/'.length);
    key = key.replace(/^\/+/, '');
    const parts = key.split('/').filter(Boolean);
    if (parts[0] !== 'trainings' || parts[1] == null || parts[1] === '' || parts[2] !== 'videos' || !parts[3]) {
        return '';
    }
    if (parts.some((part) => part === '..')) return '';
    return parts.slice(0, 4).join('/');
}

/**
 * @param {unknown} storageKey
 * @param {unknown} tenantId
 */
export function isTrainingVideoKeyForTenant(storageKey, tenantId) {
    const key = toTrainingVideoStorageKey(storageKey);
    const tenant = tenantId != null ? String(tenantId) : '';
    if (!key || !tenant) return false;
    return key.startsWith(`trainings/${tenant}/videos/`);
}

/**
 * Authenticated REST path (under the API router).
 * @param {unknown} storageKey
 */
export function trainingVideoFilePath(storageKey) {
    const key = toTrainingVideoStorageKey(storageKey);
    return key ? `${FILES_PREFIX}${key}` : '';
}

/**
 * Rewrite legacy public `/uploads/trainings/...` URLs to `/api/files/...`.
 * Other URLs pass through.
 * @param {unknown} url
 */
export function canonicalTrainingVideoUrl(url) {
    const raw = stripQuery(url);
    if (!raw) return '';
    const key = toTrainingVideoStorageKey(raw);
    return key ? trainingVideoFilePath(key) : raw;
}

/**
 * @param {unknown} storageKey
 * @returns {string[]}
 */
export function trainingVideoUrlAliases(storageKey) {
    const key = toTrainingVideoStorageKey(storageKey);
    if (!key) return [];
    return [
        key,
        `/uploads/${key}`,
        `/files/${key}`,
        `/api/files/${key}`,
        `uploads/${key}`,
        `files/${key}`,
        `api/files/${key}`,
    ];
}
